interface RGB {
  r: number;
  g: number;
  b: number;
}

interface HSL {
  h: number;
  s: number;
  l: number;
}

const DEFAULT_ACCENT = "#039fb8";
const DEFAULT_HEADER = "#102a43";

const HUE_MATCHED_DARK_L = 0.22;
const HUE_MATCHED_DARK_S_MIN = 0.3;
const HUE_MATCHED_DARK_S_MAX = 0.7;
const ACHROMATIC_THRESHOLD = 0.05;
const HOVER_DARKEN_DELTA = 0.15;
const WCAG_AA_NORMAL_TEXT = 4.5;
const WCAG_NON_TEXT_MIN = 3;
const NEUTRAL_TRACK = "#e5e5e5";

const SIDEBAR_BG_ALPHA = 0.08;
const SIDEBAR_BORDER_ALPHA = 0.18;
const ROW_ALPHA = 0.18;
const TINT_15 = 0.15;
const TINT_25 = 0.25;
const TINT_40 = 0.4;
const BORDER_ALPHA = 0.6;

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

const clamp = (v: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, v));

const normalizeHex = (hex: string): string => {
  if (!HEX_RE.test(hex)) return DEFAULT_ACCENT;
  let h = hex.replace(/^#/, "");
  if (h.length === 3)
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  return `#${h.toLowerCase()}`;
};

export const hexToRgb = (hex: string): RGB => {
  const h = normalizeHex(hex).slice(1);
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};

const toHex2 = (n: number): string =>
  clamp(Math.round(n), 0, 255).toString(16).padStart(2, "0");

export const rgbToHex = ({ r, g, b }: RGB): string =>
  `#${toHex2(r)}${toHex2(g)}${toHex2(b)}`;

export const rgbToHsl = ({ r, g, b }: RGB): HSL => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn:
        h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
        break;
      case gn:
        h = ((bn - rn) / d + 2) / 6;
        break;
      default:
        h = ((rn - gn) / d + 4) / 6;
    }
  }
  return { h, s, l };
};

const hueToRgb = (p: number, q: number, t: number): number => {
  let tt = t;
  if (tt < 0) tt += 1;
  if (tt > 1) tt -= 1;
  if (tt < 1 / 6) return p + (q - p) * 6 * tt;
  if (tt < 1 / 2) return q;
  if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
  return p;
};

export const hslToHex = ({ h, s, l }: HSL): string => {
  if (s === 0) {
    const v = Math.round(l * 255);
    return rgbToHex({ r: v, g: v, b: v });
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return rgbToHex({
    r: Math.round(hueToRgb(p, q, h + 1 / 3) * 255),
    g: Math.round(hueToRgb(p, q, h) * 255),
    b: Math.round(hueToRgb(p, q, h - 1 / 3) * 255),
  });
};

const linearize = (c: number): number => {
  const cs = c / 255;
  return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
};

export const relativeLuminance = ({ r, g, b }: RGB): number =>
  0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);

export const contrastRatio = (a: string, b: string): number => {
  const la = relativeLuminance(hexToRgb(a));
  const lb = relativeLuminance(hexToRgb(b));
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
};

export const hueMatchedDark = (hex: string): string => {
  const hsl = rgbToHsl(hexToRgb(hex));
  const s =
    hsl.s < ACHROMATIC_THRESHOLD
      ? 0
      : clamp(hsl.s, HUE_MATCHED_DARK_S_MIN, HUE_MATCHED_DARK_S_MAX);
  return hslToHex({ h: hsl.h, s, l: HUE_MATCHED_DARK_L });
};

const blendOnWhite = (hex: string, alpha: number): string => {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHex({
    r: Math.round(r * alpha + 255 * (1 - alpha)),
    g: Math.round(g * alpha + 255 * (1 - alpha)),
    b: Math.round(b * alpha + 255 * (1 - alpha)),
  });
};

export const pickContrastText = (hex: string): string => {
  const dark = hueMatchedDark(hex);
  const whiteCR = contrastRatio(hex, "#ffffff");
  const darkCR = contrastRatio(hex, dark);
  if (whiteCR >= WCAG_AA_NORMAL_TEXT && darkCR >= WCAG_AA_NORMAL_TEXT) {
    return darkCR >= whiteCR ? dark : "#ffffff";
  }
  if (darkCR >= WCAG_AA_NORMAL_TEXT) return dark;
  if (whiteCR >= WCAG_AA_NORMAL_TEXT) return "#ffffff";
  return "#000000";
};

export const darken = (hex: string, deltaL = HOVER_DARKEN_DELTA): string => {
  const hsl = rgbToHsl(hexToRgb(hex));
  return hslToHex({ h: hsl.h, s: hsl.s, l: clamp(hsl.l - deltaL, 0, 1) });
};

export const withAlpha = (hex: string, alpha: number): string => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export interface IAccentPalette {
  accent: string;
  accentHover: string;
  accentText: string;
  accentTextDark: string;
  /** Accent guaranteed to have ≥3:1 contrast on a white background (falls back to accentTextDark). */
  accentOnWhite: string;
  /** Readable text/icon color on top of accentOnWhite. */
  accentOnWhiteText: string;
  chipText: string;
  tint15: string;
  tint25: string;
  tint40: string;
  border: string;
  row: string;
  sidebarBg: string;
  sidebarBorder: string;
  headerBg: string;
  headerMedium: string;
  headerText: string;
}

export const buildAccentPalette = (
  accentColor: string | undefined,
  headerColor: string | undefined,
): IAccentPalette => {
  const accent = normalizeHex(accentColor || DEFAULT_ACCENT);
  const header = normalizeHex(headerColor || DEFAULT_HEADER);
  const accentTextDark = hueMatchedDark(accent);
  const accentOnWhite =
    contrastRatio(accent, "#ffffff") >= WCAG_NON_TEXT_MIN
      ? accent
      : accentTextDark;
  return {
    accent,
    accentHover: darken(accent),
    accentText: pickContrastText(accent),
    accentTextDark,
    accentOnWhite,
    accentOnWhiteText: pickContrastText(accentOnWhite),
    chipText: pickContrastText(blendOnWhite(accent, TINT_25)),
    tint15: withAlpha(accent, TINT_15),
    tint25: withAlpha(accent, TINT_25),
    tint40: withAlpha(accent, TINT_40),
    border: withAlpha(accent, BORDER_ALPHA),
    row: withAlpha(accent, ROW_ALPHA),
    sidebarBg: withAlpha(header, SIDEBAR_BG_ALPHA),
    sidebarBorder: withAlpha(header, SIDEBAR_BORDER_ALPHA),
    headerBg: header,
    headerMedium: withAlpha(header, SIDEBAR_BORDER_ALPHA),
    headerText: pickContrastText(header),
  };
};

export interface AccentContrastReport {
  badgeText: number;
  progressFillVsTrack: number;
  passesBadgeText: boolean;
  passesProgressFill: boolean;
}

export const evaluateAccentContrast = (
  accentHex: string,
): AccentContrastReport => {
  const accent = normalizeHex(accentHex);
  const text = pickContrastText(accent);
  const badgeText = contrastRatio(accent, text);
  const progressFillVsTrack = contrastRatio(accent, NEUTRAL_TRACK);
  return {
    badgeText,
    progressFillVsTrack,
    passesBadgeText: badgeText >= WCAG_AA_NORMAL_TEXT,
    passesProgressFill: progressFillVsTrack >= WCAG_NON_TEXT_MIN,
  };
};
